import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, ArrowLeft, Building2, Trash2, Image as ImageIcon, Film, FileText, X, QrCode, Copy, Check, ExternalLink, CalendarRange, MapPin, Users, Euro, Sparkles, BookOpen, ScrollText, RefreshCw, Eye, MessageSquare, LayoutGrid, Rows3, SlidersHorizontal, type LucideIcon } from 'lucide-react';
import {
  useProperties, useSaveProperty, useDeleteProperty, useBookings,
  usePropertyMedia, useUploadPropertyMedia, useDeletePropertyMedia,
  useSeasons, useSaveSeason, useDeleteSeason, useOwners, useSaveOwner,
  type Property, type PropertyMedia, type Season, type Booking,
} from '../../../hooks/useManagement';
import { useOnboarding } from '../../../hooks/useOnboarding';
import { AutoTextarea } from '../../../components/AutoTextarea';
import { AMENITIES, AMENITY_GROUPS, parseAmenities } from '../../../lib/amenities';
import { useConfirm } from '../../../components/ConfirmDialog';
import { useToast } from '../../../components/ToastProvider';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import SaveButton from '../../../components/SaveButton';

type Draft = Partial<Property> & { id?: number };
const EMPTY: Draft = { name: '', status: 'active', check_in_time: '15:00', check_out_time: '11:00', country: 'España' };

// Optional columns for the table view of "Mis alojamientos" (name is always shown).
// The table view doubles as the agency "portfolio" view: occupancy / revenue / ADR /
// next arrival / attention are computed from bookings.
type ColKey = 'city' | 'type' | 'guests' | 'beds' | 'price' | 'owner' | 'owner_email' | 'registration'
  | 'occupancy' | 'revenue' | 'adr' | 'next' | 'attention' | 'status';
const ALL_COLS: { key: ColKey; label: string }[] = [
  { key: 'occupancy', label: 'Ocupación 30 d' },
  { key: 'revenue', label: 'Ingresos' },
  { key: 'adr', label: 'ADR' },
  { key: 'next', label: 'Próxima llegada' },
  { key: 'attention', label: 'Atención' },
  { key: 'city', label: 'Ciudad' },
  { key: 'type', label: 'Tipo' },
  { key: 'guests', label: 'Huéspedes' },
  { key: 'beds', label: 'Camas' },
  { key: 'price', label: '€/noche' },
  { key: 'owner', label: 'Propietario' },
  { key: 'owner_email', label: 'Email propietario' },
  { key: 'registration', label: 'Licencia' },
  { key: 'status', label: 'Estado' },
];
const DEFAULT_COLS: ColKey[] = ['occupancy', 'revenue', 'next', 'owner', 'attention', 'status'];
const WINDOW = 30; // days for forward-looking occupancy
const VIEW_KEY = 'kx_props_view';
const COLS_KEY = 'kx_props_cols';

interface Metrics { revenue: number; adr: number; occupancy: number; nextArrival: string | null; attention: string[] }

type TFn = (k: string, d?: string | Record<string, unknown>, o?: Record<string, unknown>) => string;

// Per-property portfolio metrics from bookings (forward occupancy + revenue + ADR
// + next arrival + attention flags). Plain Date math — day-level precision.
function computeMetrics(properties: Property[], bookings: Booking[], t: TFn): Map<number, Metrics> {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const t0 = today.getTime();
  const windowEnd = t0 + WINDOW * 86400_000;
  const byProp = new Map<number, Booking[]>();
  for (const b of bookings) {
    if (b.property_id == null || b.status === 'cancelled') continue;
    const arr = byProp.get(b.property_id) ?? []; arr.push(b); byProp.set(b.property_id, arr);
  }
  const out = new Map<number, Metrics>();
  for (const p of properties) {
    const list = byProp.get(p.id) ?? [];
    let revenue = 0, revNights = 0, windowNights = 0, pending = 0;
    let nextArrival: string | null = null;
    for (const b of list) {
      if (b.status === 'confirmed' || b.status === 'completed') { revenue += b.total || 0; revNights += b.nights || 0; }
      if (b.status === 'pending') pending++;
      const ci = new Date(b.check_in).getTime(), co = new Date(b.check_out).getTime();
      if (co > t0 && ci < windowEnd) {
        windowNights += Math.max(0, Math.round((Math.min(windowEnd, co) - Math.max(t0, ci)) / 86400_000));
      }
      if ((b.status === 'confirmed' || b.status === 'pending') && ci >= t0 && (!nextArrival || b.check_in < nextArrival)) nextArrival = b.check_in;
    }
    const attention: string[] = [];
    const occupancy = Math.min(1, windowNights / WINDOW);
    if (occupancy === 0) attention.push(t('portal.manage.portfolio.flagEmpty', 'Sin reservas próximas'));
    if (pending > 0) attention.push(t('portal.manage.portfolio.flagPending', '{{count}} por confirmar', { count: pending }));
    if (!p.base_price) attention.push(t('portal.manage.portfolio.flagNoPrice', 'Sin precio'));
    if (p.status !== 'active') attention.push(t('portal.manage.portfolio.flagPaused', 'Pausado'));
    out.set(p.id, { revenue, adr: revNights ? revenue / revNights : 0, occupancy, nextArrival, attention });
  }
  return out;
}
const eurM = (n: number) => `€${Math.round(n).toLocaleString('es-ES')}`;

const PROPERTY_TYPES: [string, string][] = [
  ['', '—'],
  ['apartamento', 'Apartamento'], ['casa', 'Casa'], ['villa', 'Villa'],
  ['estudio', 'Estudio'], ['habitacion', 'Habitación'], ['chalet', 'Chalet'],
  ['atico', 'Ático'], ['bungalow', 'Bungalow'], ['otro', 'Otro'],
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
const input = 'w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm';

// A grouped, titled card used to organise the property editor.
function EditorSection({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 break-inside-avoid mb-6">
      <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Icon size={17} className="text-teal-600" /> {title}</h2>
      {children}
    </section>
  );
}

// Admin control for the guest-app guide: toggle built-in sections on/off, add
// custom sections, and configure arrival pickup — all stored in the property's
// guest_sections_json. The interactive guest app renders from this.
interface CustomSection { id: string; emoji: string; title: string; body: string }
interface SectionsCfg { hidden: string[]; custom: CustomSection[]; pickup: { enabled: boolean; info: string } }
// The guide sections the guest app can show/hide (1:1 with GuideScreen in the
// guest app). Access/keybox + parking live on the Home screen and are essential
// to the flow, so they are not hideable here.
const BUILTIN_SECTIONS = ['wifi', 'manual', 'houserules', 'area', 'checkout'] as const;

function parseCfg(v: string | null): SectionsCfg {
  try {
    const o = v ? JSON.parse(v) : {};
    return {
      hidden: Array.isArray(o.hidden) ? o.hidden : [],
      custom: Array.isArray(o.custom) ? o.custom : [],
      pickup: { enabled: !!o.pickup?.enabled, info: o.pickup?.info || '' },
    };
  } catch { return { hidden: [], custom: [], pickup: { enabled: false, info: '' } }; }
}

function GuestSectionsConfig({ value, onChange }: { value: string | null; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const cfg = parseCfg(value);
  const write = (next: SectionsCfg) => onChange(JSON.stringify(next));
  const sectionLabel = (k: string) => t(`portal.manage.properties.guideSection.${k}`, k);
  const toggle = (k: string) => {
    const hidden = cfg.hidden.includes(k) ? cfg.hidden.filter(x => x !== k) : [...cfg.hidden, k];
    write({ ...cfg, hidden });
  };
  const addCustom = () => write({ ...cfg, custom: [...cfg.custom, { id: crypto.randomUUID().slice(0, 8), emoji: '📌', title: '', body: '' }] });
  const setCustom = (id: string, patch: Partial<CustomSection>) => write({ ...cfg, custom: cfg.custom.map(c => c.id === id ? { ...c, ...patch } : c) });
  const delCustom = (id: string) => write({ ...cfg, custom: cfg.custom.filter(c => c.id !== id) });

  const input = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500';
  return (
    <div className="pt-2 border-t border-slate-100 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t('portal.manage.properties.guideSectionsTitle', 'Secciones visibles en la app')}</p>
      <div className="flex flex-wrap gap-2">
        {BUILTIN_SECTIONS.map(k => {
          const on = !cfg.hidden.includes(k);
          return (
            <button key={k} type="button" onClick={() => toggle(k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border ${on ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-slate-50 border-slate-200 text-slate-400 line-through'}`}>
              <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${on ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300'}`}>{on && <Check size={10} />}</span>
              {sectionLabel(k)}
            </button>
          );
        })}
      </div>

      {/* Pickup / arrival */}
      <div className="rounded-lg border border-slate-200 p-3">
        <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
          <input type="checkbox" checked={cfg.pickup.enabled} onChange={e => write({ ...cfg, pickup: { ...cfg.pickup, enabled: e.target.checked } })} />
          {t('portal.manage.properties.guidePickupEnable', 'Ofrecer «solicitar recogida» en el check-in')}
        </label>
        {cfg.pickup.enabled && (
          <AutoTextarea className={`${input} mt-2`} value={cfg.pickup.info} onChange={e => write({ ...cfg, pickup: { ...cfg.pickup, info: e.target.value } })}
            placeholder={t('portal.manage.properties.guidePickupPlaceholder', 'Qué verá el huésped al solicitar recogida (precio, cómo se coordina, contacto…)')} />
        )}
      </div>

      {/* Custom sections */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t('portal.manage.properties.guideCustomTitle', 'Secciones personalizadas')}</p>
          <button type="button" onClick={addCustom} className="inline-flex items-center gap-1 text-sm text-teal-700 hover:bg-teal-50 px-2 py-1 rounded-lg"><Plus size={14} /> {t('portal.manage.properties.guideCustomAdd', 'Añadir sección')}</button>
        </div>
        {cfg.custom.map(c => (
          <div key={c.id} className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="flex gap-2">
              <input value={c.emoji} onChange={e => setCustom(c.id, { emoji: e.target.value.slice(0, 2) })} className="w-14 text-center px-2 py-2 border border-slate-300 rounded-lg text-lg" />
              <input value={c.title} onChange={e => setCustom(c.id, { title: e.target.value })} placeholder={t('portal.manage.properties.guideCustomTitlePh', 'Título de la sección')} className={input} />
              <button type="button" onClick={() => delCustom(c.id)} className="shrink-0 p-2 text-rose-500 hover:bg-rose-50 rounded-lg"><Trash2 size={15} /></button>
            </div>
            <AutoTextarea value={c.body} onChange={e => setCustom(c.id, { body: e.target.value })} placeholder={t('portal.manage.properties.guideCustomBodyPh', 'Contenido…')} className={input} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Length-of-stay price tiers ───────────────────────────────────────────────
// Absolute per-night prices that get cheaper the longer the stay. Stored inline
// as JSON on the property (price_tiers_json). The booking engine applies the
// tier with the greatest min_nights <= stay length (overriding base/season) and
// skips the coarse weekly/monthly % when a tier matches.
interface PriceTier { min_nights: number; price_per_night: number }
function parseTiers(v: string | null | undefined): PriceTier[] {
  try {
    const a = v ? JSON.parse(v) : [];
    if (!Array.isArray(a)) return [];
    return a.filter(x => x && typeof x === 'object')
      .map(x => ({ min_nights: Math.floor(Number((x as PriceTier).min_nights)) || 0, price_per_night: Number((x as PriceTier).price_per_night) || 0 }));
  } catch { return []; }
}
function PriceTiersEditor({ value, basePrice, onChange }: { value: string | null | undefined; basePrice: number | null; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const tiers = parseTiers(value);
  // Keep insertion order while editing (avoid rows jumping as the admin types a
  // min-nights value); the engine sorts on read.
  const write = (next: PriceTier[]) => onChange(JSON.stringify(next));
  const add = () => {
    const last = tiers[tiers.length - 1];
    write([...tiers, {
      min_nights: last ? last.min_nights + 7 : 7,
      price_per_night: last ? last.price_per_night : (basePrice ?? 0),
    }]);
  };
  const setRow = (i: number, patch: Partial<PriceTier>) => write(tiers.map((tr, idx) => idx === i ? { ...tr, ...patch } : tr));
  const del = (i: number) => write(tiers.filter((_, idx) => idx !== i));
  const input = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500';

  return (
    <div className="pt-3 mt-3 border-t border-slate-100 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-700">{t('portal.manage.properties.tiersTitle', 'Precios por duración de estancia')}</p>
          <p className="text-xs text-slate-400 mt-0.5">{t('portal.manage.properties.tiersHint', 'Cuanto más larga la estancia, menor el precio por noche. El tramo con la duración mínima más alta que no supere la estancia manda.')}</p>
        </div>
        <button type="button" onClick={add} className="shrink-0 inline-flex items-center gap-1 text-sm text-teal-700 hover:bg-teal-50 px-2 py-1 rounded-lg"><Plus size={14} /> {t('portal.manage.properties.tiersAdd', 'Añadir tramo')}</button>
      </div>

      {/* Implicit baseline row: below the smallest tier, base price applies. */}
      <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
        <span className="font-medium">{t('portal.manage.properties.tiersBaseRow', 'Estancias cortas')}</span>
        <span className="text-slate-400">·</span>
        <span>{t('portal.manage.properties.tiersBaseRowVal', '{{price}} €/noche (precio base)', { price: basePrice ?? 0 })}</span>
      </div>

      {tiers.length === 0 ? (
        <p className="text-xs text-slate-400 italic">{t('portal.manage.properties.tiersEmpty', 'Sin tramos: se usa el precio base (y los descuestos semanal/mensual si están configurados).')}</p>
      ) : (
        <div className="space-y-2">
          {tiers.map((tr, i) => (
            <div key={i} className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap">
                {t('portal.manage.properties.tiersFrom', 'Desde')}
                <input type="number" min={1} className="w-20 px-2 py-2 border border-slate-300 rounded-lg text-sm" value={tr.min_nights || ''} onChange={e => setRow(i, { min_nights: Math.max(1, Math.floor(Number(e.target.value)) || 0) })} />
                {t('portal.manage.properties.tiersNights', 'noches')}
              </label>
              <span className="text-slate-300">→</span>
              <label className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap flex-1">
                <input type="number" min={0} step="0.01" className={input} value={tr.price_per_night || ''} onChange={e => setRow(i, { price_per_night: Math.max(0, Number(e.target.value) || 0) })} />
                {t('portal.manage.properties.tiersPerNight', '€/noche')}
              </label>
              <button type="button" onClick={() => del(i)} className="shrink-0 p-2 text-rose-500 hover:bg-rose-50 rounded-lg"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Owner select for the property editor: pick an existing owner or add a new one
// inline (created + assigned in one step).
function OwnerPicker({ value, onChange }: { value: number | null; onChange: (id: number | null) => void }) {
  const { t } = useTranslation();
  const { data: owners } = useOwners();
  const saveOwner = useSaveOwner();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await saveOwner.mutateAsync({ name: name.trim(), email: email.trim() || undefined });
      if (r.id) onChange(r.id);
      setAdding(false); setName(''); setEmail('');
    } finally { setSaving(false); }
  };

  const input = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500';
  return (
    <div>
      <span className="block text-xs font-medium text-slate-600 mb-1">{t('portal.manage.properties.fieldOwner', 'Propietario')}</span>
      {!adding ? (
        <div className="flex gap-2">
          <select value={value ?? ''} onChange={e => onChange(e.target.value ? Number(e.target.value) : null)} className={input}>
            <option value="">{t('portal.manage.properties.ownerNone', '— Sin propietario —')}</option>
            {(owners ?? []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button type="button" onClick={() => setAdding(true)} className="shrink-0 inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-lg text-sm text-teal-700 hover:bg-teal-50 whitespace-nowrap">
            <Plus size={14} /> {t('portal.manage.properties.ownerNew', 'Nuevo')}
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3 space-y-2">
          <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder={t('portal.manage.owners.name', 'Nombre del propietario')} className={input} />
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="owner@email.com" className={input} />
          <div className="flex gap-2">
            <button type="button" onClick={create} disabled={saving || !name.trim()} className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {t('portal.manage.properties.ownerAdd', 'Añadir y asignar')}
            </button>
            <button type="button" onClick={() => setAdding(false)} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">{t('portal.manage.owners.cancel', 'Cancelar')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ManagePropertiesPage() {
  const { t } = useTranslation();
  const { data: properties, isLoading } = useProperties();
  const { data: onboarding } = useOnboarding();
  const save = useSaveProperty();
  const del = useDeleteProperty();
  const [editing, setEditing] = useState<Draft | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  const [view, setView] = useState<'grid' | 'table'>(() => (localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'grid'));
  const [cols, setCols] = useState<ColKey[]>(() => {
    try { const s = JSON.parse(localStorage.getItem(COLS_KEY) || 'null'); return Array.isArray(s) && s.length ? s : DEFAULT_COLS; } catch { return DEFAULT_COLS; }
  });
  const [colsOpen, setColsOpen] = useState(false);
  const [onlyAttention, setOnlyAttention] = useState(false);
  // Portfolio metrics (table view doubles as the agency portfolio dashboard).
  const { data: bookings } = useBookings('all', null);
  const metrics = useMemo(() => computeMetrics(properties ?? [], bookings ?? [], t as unknown as TFn), [properties, bookings, t]);
  const setViewP = (v: 'grid' | 'table') => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* */ } };
  const toggleCol = (k: ColKey) => setCols(prev => {
    const next = prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k];
    try { localStorage.setItem(COLS_KEY, JSON.stringify(next)); } catch { /* */ }
    return next;
  });
  const askDelete = async (p: Property) => {
    if ((await confirm({ title: t('portal.manage.properties.deleteTitle', 'Eliminar alojamiento'), message: t('portal.manage.properties.deleteConfirm', { name: p.name }), confirmLabel: t('portal.manage.properties.deleteYes', 'Sí, eliminar'), tone: 'danger' })) === 'confirm') del.mutate(p.id);
  };

  // The editor's open-state lives in the URL (?edit=<id> for an existing property,
  // ?edit=new for a fresh one). This makes edit links shareable/back-button
  // friendly AND — crucially — means navigating to this page WITHOUT ?edit (e.g.
  // clicking the "Mis alojamientos" menu item while already on the page) exits the
  // editor instead of getting stuck in it.
  const editId = searchParams.get('edit');
  useEffect(() => {
    if (!editId) { setEditing(cur => (cur ? null : cur)); return; }   // param cleared → close
    if (editId === 'new' || !properties) return;                     // 'new' handled by openEditor
    const p = properties.find(x => String(x.id) === editId);
    if (p) setEditing(cur => (cur && cur.id === p.id ? cur : p));     // don't re-seed the same editor
  }, [editId, properties]);

  const openEditor = (draft: Draft) => {
    setEditing(draft);
    setSearchParams(prev => { prev.set('edit', draft.id ? String(draft.id) : 'new'); return prev; });
  };
  const closeEditor = () => {
    setEditing(null);
    setSearchParams(prev => { prev.delete('edit'); return prev; });
  };

  // Pre-fill a NEW property with the owner's "same for all properties" defaults
  // (captured on the Business Details onboarding step). Owners can override.
  const newDraft = (): Draft => {
    const d = onboarding?.profile.data?.defaults;
    return {
      ...EMPTY,
      check_in_time: d?.checkInTime || EMPTY.check_in_time,
      check_out_time: d?.checkOutTime || EMPTY.check_out_time,
      house_rules: d?.houseRules || '',
      cancellation_policy: d?.cancellationPolicy || '',
    };
  };

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal-500" size={32} /></div>;

  // ── Editor ──
  if (editing) {
    return <PropertyEditor draft={editing} onClose={closeEditor} save={save} />;
  }

  // ── List ──
  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('portal.manage.properties.title')}</h1>
          <p className="text-slate-500 mt-0.5">{t('portal.manage.properties.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex items-center rounded-lg bg-slate-100 p-1">
            <button onClick={() => setViewP('grid')} title={t('portal.manage.properties.viewGrid', 'Cuadrícula')} className={`p-1.5 rounded-md ${view === 'grid' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`}><LayoutGrid size={16} /></button>
            <button onClick={() => setViewP('table')} title={t('portal.manage.properties.viewTable', 'Tabla')} className={`p-1.5 rounded-md ${view === 'table' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`}><Rows3 size={16} /></button>
          </div>
          {/* Column picker (table only) */}
          {view === 'table' && (
            <div className="relative">
              <button onClick={() => setColsOpen(o => !o)} onBlur={() => setTimeout(() => setColsOpen(false), 150)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                <SlidersHorizontal size={15} /> {t('portal.manage.properties.columns', 'Columnas')}
              </button>
              {colsOpen && (
                <div className="absolute right-0 mt-1.5 z-30 w-52 rounded-xl border border-slate-200 bg-white shadow-xl py-1">
                  {ALL_COLS.map(c => (
                    <button key={c.key} onMouseDown={e => e.preventDefault()} onClick={() => toggleCol(c.key)} className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 text-left">
                      <span className={`w-4 h-4 rounded border flex items-center justify-center ${cols.includes(c.key) ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300'}`}>{cols.includes(c.key) && <Check size={12} />}</span>
                      {t(`portal.manage.properties.col.${c.key}`, c.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button onClick={() => openEditor(newDraft())}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg">
            <Plus size={16} /> {t('portal.manage.properties.addProperty')}
          </button>
        </div>
      </div>

      {(!properties || properties.length === 0) ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
          <Building2 className="mx-auto text-slate-300" size={40} />
          <p className="mt-3 font-semibold text-slate-700">{t('portal.manage.properties.emptyTitle')}</p>
          <p className="text-sm text-slate-500 mt-1">{t('portal.manage.properties.emptySub')}</p>
          <button onClick={() => openEditor(newDraft())}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg">
            <Plus size={16} /> {t('portal.manage.properties.createFirst')}
          </button>
        </div>
      ) : (
        view === 'grid' ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {properties.map(p => (
            <div key={p.id} className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all relative">
              <button onClick={() => openEditor(p)} className="text-left w-full">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  {(p as { cover_photo_id?: number }).cover_photo_id ? (
                    <img
                      src={`/api/portal/manage/property-media/${(p as { cover_photo_id?: number }).cover_photo_id}`}
                      alt={p.name || ''}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 text-3xl">🏠</div>
                  )}
                </div>
                <div className="p-4">
                  <p className="font-semibold text-slate-900 truncate">{p.name || t('portal.manage.properties.unnamed')}</p>
                  <p className="text-sm text-slate-500 truncate mt-0.5">{[p.city, p.max_guests && t('portal.manage.properties.guestsShort', { count: p.max_guests }), p.base_price && t('portal.manage.properties.perNight', { price: p.base_price })].filter(Boolean).join(' · ') || t('portal.manage.properties.completeDetails')}</p>
                  <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${p.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{p.status === 'active' ? t('portal.manage.properties.statusActive') : t('portal.manage.properties.statusPaused')}</span>
                </div>
              </button>
              <button onClick={() => askDelete(p as Property)} aria-label={t('portal.manage.properties.delete')} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-slate-400 hover:text-rose-500 shadow-sm"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        ) : (
        <>
        {/* Portfolio KPIs + attention filter (table view = agency portfolio view) */}
        {(() => {
          const revenue = properties.reduce((s, p) => s + (metrics.get(p.id)?.revenue ?? 0), 0);
          const occ = properties.length ? properties.reduce((s, p) => s + (metrics.get(p.id)?.occupancy ?? 0), 0) / properties.length : 0;
          const attn = properties.filter(p => (metrics.get(p.id)?.attention.length ?? 0) > 0).length;
          return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <MiniKpi label={t('portal.manage.portfolio.kpiProps', 'Alojamientos')} value={String(properties.length)} />
              <MiniKpi label={t('portal.manage.portfolio.kpiOcc', 'Ocupación 30 d')} value={`${Math.round(occ * 100)}%`} />
              <MiniKpi label={t('portal.manage.portfolio.kpiRevenue', 'Ingresos (directos)')} value={eurM(revenue)} />
              <MiniKpi label={t('portal.manage.portfolio.kpiAttention', 'Requieren atención')} value={String(attn)} tone={attn ? 'amber' : 'slate'} />
            </div>
          );
        })()}
        <label className="mb-3 inline-flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={onlyAttention} onChange={e => setOnlyAttention(e.target.checked)} className="rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
          {t('portal.manage.portfolio.onlyAttention', 'Solo los que requieren atención')}
        </label>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.properties.col.name', 'Alojamiento')}</th>
                {ALL_COLS.filter(c => cols.includes(c.key)).map(c => (
                  <th key={c.key} className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">{t(`portal.manage.properties.col.${c.key}`, c.label)}</th>
                ))}
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {properties.filter(p => !onlyAttention || (metrics.get(p.id)?.attention.length ?? 0) > 0).map(p => {
                const cover = (p as { cover_photo_id?: number }).cover_photo_id;
                const mx = metrics.get(p.id);
                const cell = (k: ColKey) => {
                  switch (k) {
                    case 'city': return p.city || '—';
                    case 'type': return p.property_type ? t(`portal.manage.properties.types.${p.property_type}`, p.property_type) : '—';
                    case 'guests': return p.max_guests ?? '—';
                    case 'beds': return p.beds ?? p.bedrooms ?? '—';
                    case 'price': return p.base_price ? `€${p.base_price}` : '—';
                    case 'owner': return p.owner_display_name || p.owner_name || '—';
                    case 'owner_email': return p.owner_email || '—';
                    case 'registration': return p.registration_number || '—';
                    case 'occupancy': {
                      const o = mx?.occupancy ?? 0;
                      return (
                        <span className="inline-flex items-center gap-2 w-32">
                          <span className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><span className={`block h-full ${o === 0 ? 'bg-rose-400' : o < 0.4 ? 'bg-amber-400' : 'bg-emerald-500'}`} style={{ width: `${Math.round(o * 100)}%` }} /></span>
                          <span className="text-xs text-slate-500 w-8 text-right">{Math.round(o * 100)}%</span>
                        </span>
                      );
                    }
                    case 'revenue': return mx?.revenue ? eurM(mx.revenue) : '—';
                    case 'adr': return mx?.adr ? eurM(mx.adr) : '—';
                    case 'next': return mx?.nextArrival || '—';
                    case 'attention': return mx?.attention.length
                      ? <span className="flex flex-wrap gap-1">{mx.attention.map((a, i) => <span key={i} className="inline-block rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[11px] font-medium">{a}</span>)}</span>
                      : <span className="text-emerald-600 text-xs font-medium">✓ OK</span>;
                    case 'status': return (
                      <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${p.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{p.status === 'active' ? t('portal.manage.properties.statusActive') : t('portal.manage.properties.statusPaused')}</span>
                    );
                    default: return '—';
                  }
                };
                return (
                  <tr key={p.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openEditor(p)}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                          {cover ? <img src={`/api/portal/manage/property-media/${cover}`} alt="" loading="lazy" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300">🏠</div>}
                        </div>
                        <span className="font-medium text-slate-900 truncate max-w-[16rem]">{p.name || t('portal.manage.properties.unnamed')}</span>
                      </div>
                    </td>
                    {ALL_COLS.filter(c => cols.includes(c.key)).map(c => (
                      <td key={c.key} className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{cell(c.key)}</td>
                    ))}
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={(e) => { e.stopPropagation(); askDelete(p as Property); }} aria-label={t('portal.manage.properties.delete')} className="text-slate-300 hover:text-rose-500"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
        )
      )}
    </div>
  );
}

function MiniKpi({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'amber' }) {
  return (
    <div className={`rounded-xl border p-3 ${tone === 'amber' ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <div className={`text-xl font-bold ${tone === 'amber' ? 'text-amber-700' : 'text-slate-900'}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function PropertyEditor({ draft, onClose, save }: { draft: Draft; onClose: () => void; save: ReturnType<typeof useSaveProperty> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState<Draft>(draft);
  const { isDirty, resetBaseline } = useDirtyForm(form);
  const set = (k: keyof Property, v: unknown) => setForm(f => ({ ...f, [k]: v }));
  const num = (v: string) => (v === '' ? null : Number(v));

  const onSave = async () => {
    try {
      const res = await save.mutateAsync(form);
      // Keep editing the now-saved property (enables media upload etc.).
      const saved = res.id && !form.id ? { ...form, id: res.id } : form;
      if (saved !== form) setForm(saved);
      resetBaseline(saved);   // baseline := saved → Save shows "Guardado" (disabled)
      toast.success(t('portal.manage.properties.saved', 'Alojamiento guardado'));
    } catch {
      toast.error(t('portal.manage.properties.saveError', 'No se ha podido guardar. Inténtalo de nuevo.'));
    }
  };
  const nameValid = !!(form.name && form.name.trim());

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-24">
      <button onClick={onClose} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-700"><ArrowLeft size={16} /> {t('portal.manage.properties.backToList')}</button>

      <h1 className="text-2xl font-bold text-slate-900">{form.id ? form.name || t('portal.manage.properties.editProperty') : t('portal.manage.properties.newProperty')}</h1>

      {/* Sections flow into two columns on wide screens (each card stays intact). */}
      <div className="xl:columns-2 xl:gap-6">
      {/* Basics */}
      <EditorSection icon={Building2} title={t('portal.manage.properties.basicsTitle')}>
        <Field label={t('portal.manage.properties.fieldNameRequired')}><input className={input} value={form.name ?? ''} onChange={e => set('name', e.target.value)} placeholder={t('portal.manage.properties.namePlaceholder')} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('portal.manage.properties.fieldType', 'Tipo de alojamiento')}>
            <select className={input} value={form.property_type ?? ''} onChange={e => set('property_type', e.target.value)}>
              {PROPERTY_TYPES.map(([v, l]) => <option key={v} value={v}>{v ? t(`portal.manage.properties.types.${v}`, l) : l}</option>)}
            </select>
          </Field>
          <Field label={t('portal.manage.properties.fieldStatus')}>
            <select className={input} value={form.status ?? 'active'} onChange={e => set('status', e.target.value)}>
              <option value="active">{t('portal.manage.properties.statusActive')}</option><option value="paused">{t('portal.manage.properties.statusPaused')}</option>
            </select>
          </Field>
        </div>
        <Field label={t('portal.manage.properties.fieldLicense')}><input className={input} value={form.registration_number ?? ''} onChange={e => set('registration_number', e.target.value)} placeholder={t('portal.manage.properties.licensePlaceholder', 'Nº de registro / licencia turística')} /></Field>
        <OwnerPicker value={form.owner_id ?? null} onChange={id => set('owner_id', id)} />
      </EditorSection>

      {/* Capacity */}
      <EditorSection icon={Users} title={t('portal.manage.properties.capacityTitle', 'Capacidad')}>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Field label={t('portal.manage.properties.fieldGuests')}><input type="number" className={input} value={form.max_guests ?? ''} onChange={e => set('max_guests', num(e.target.value))} /></Field>
          <Field label={t('portal.manage.properties.fieldBedrooms')}><input type="number" className={input} value={form.bedrooms ?? ''} onChange={e => set('bedrooms', num(e.target.value))} /></Field>
          <Field label={t('portal.manage.properties.fieldBeds', 'Camas')}><input type="number" className={input} value={form.beds ?? ''} onChange={e => set('beds', num(e.target.value))} /></Field>
          <Field label={t('portal.manage.properties.fieldBathrooms')}><input type="number" className={input} value={form.bathrooms ?? ''} onChange={e => set('bathrooms', num(e.target.value))} /></Field>
          <Field label={t('portal.manage.properties.fieldSurface')}><input type="number" className={input} value={form.surface_m2 ?? ''} onChange={e => set('surface_m2', num(e.target.value))} /></Field>
        </div>
      </EditorSection>

      {/* Location */}
      <EditorSection icon={MapPin} title={t('portal.manage.properties.locationTitle')}>
        <Field label={t('portal.manage.properties.fieldAddress')}><input className={input} value={form.address_street ?? ''} onChange={e => set('address_street', e.target.value)} /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label={t('portal.manage.properties.fieldPostCode', 'Código postal')}><input className={input} value={form.post_code ?? ''} onChange={e => set('post_code', e.target.value)} /></Field>
          <Field label={t('portal.manage.properties.fieldCity')}><input className={input} value={form.city ?? ''} onChange={e => set('city', e.target.value)} /></Field>
          <Field label={t('portal.manage.properties.fieldCountry', 'País')}><input className={input} value={form.country ?? ''} onChange={e => set('country', e.target.value)} placeholder={t('portal.manage.properties.countryPlaceholder', 'España')} /></Field>
        </div>
      </EditorSection>

      {/* Stay rules */}
      <EditorSection icon={CalendarRange} title={t('portal.manage.properties.stayRulesTitle', 'Reglas de estancia')}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label={t('portal.manage.properties.checkIn')}><input className={input} value={form.check_in_time ?? ''} onChange={e => set('check_in_time', e.target.value)} placeholder="15:00" /></Field>
          <Field label={t('portal.manage.properties.checkOut')}><input className={input} value={form.check_out_time ?? ''} onChange={e => set('check_out_time', e.target.value)} placeholder="11:00" /></Field>
          <Field label={t('portal.manage.properties.fieldMinNights', 'Noches mín.')}><input type="number" className={input} value={form.min_nights ?? ''} onChange={e => set('min_nights', num(e.target.value))} /></Field>
          <Field label={t('portal.manage.properties.fieldMaxNights', 'Noches máx.')}><input type="number" className={input} value={form.max_nights ?? ''} onChange={e => set('max_nights', num(e.target.value))} /></Field>
        </div>
      </EditorSection>

      {/* Pricing */}
      <EditorSection icon={Euro} title={t('portal.manage.properties.pricingTitle')}>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t('portal.manage.properties.fieldBasePrice')}><input type="number" className={input} value={form.base_price ?? ''} onChange={e => set('base_price', num(e.target.value))} /></Field>
          <Field label={t('portal.manage.properties.fieldCleaning')}><input type="number" className={input} value={form.cleaning_fee ?? ''} onChange={e => set('cleaning_fee', num(e.target.value))} /></Field>
          <Field label={t('portal.manage.properties.fieldDeposit')}><input type="number" className={input} value={form.security_deposit ?? ''} onChange={e => set('security_deposit', num(e.target.value))} /></Field>
        </div>

        {/* Length-of-stay price tiers (inline JSON — no id needed) */}
        <PriceTiersEditor value={form.price_tiers_json} basePrice={form.base_price ?? null} onChange={v => set('price_tiers_json', v)} />

        {/* Seasonal pricing — only once the property exists (needs an id) */}
        {form.id ? (
          <SeasonsEditor propertyId={form.id} basePrice={form.base_price ?? null} />
        ) : (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 text-center">
            {t('portal.manage.properties.seasonsLocked', 'Guarda el alojamiento para añadir precios por temporada.')}
          </div>
        )}
      </EditorSection>

      {/* Amenities picker */}
      <EditorSection icon={Sparkles} title={t('portal.manage.properties.amenitiesTitle', 'Comodidades')}>
        <AmenityPicker
          selected={parseAmenities(form.amenities_json)}
          onChange={(keys) => set('amenities_json', JSON.stringify(keys))}
        />
        <Field label={t('portal.manage.properties.fieldOtherAmenities', 'Otras comodidades')}>
          <AutoTextarea className={input} value={form.amenities ?? ''} onChange={e => set('amenities', e.target.value)} placeholder={t('portal.manage.properties.otherAmenitiesPlaceholder', 'Cualquier comodidad que no esté en la lista…')} />
        </Field>
      </EditorSection>

      {/* Description */}
      <EditorSection icon={FileText} title={t('portal.manage.properties.descriptionTitle')}>
        <Field label={t('portal.manage.properties.fieldDescription')}><AutoTextarea className={input} rows={4} value={form.description ?? ''} onChange={e => set('description', e.target.value)} placeholder={t('portal.manage.properties.descriptionPlaceholder')} /></Field>
      </EditorSection>

      {/* Guest guide (per property) — THIS is the guest-app editor. The guest app
          at /guest/<token> is data-driven from these very fields. */}
      <EditorSection icon={BookOpen} title={t('portal.manage.properties.guideTitle', 'Guía del huésped')}>
        <div className="rounded-lg bg-teal-50 border border-teal-100 p-3 -mt-1">
          <p className="text-sm text-teal-800">
            {t('portal.manage.properties.guideAppNote', 'Esto es lo que verán tus huéspedes en la guía digital (app del huésped) de este alojamiento.')}
          </p>
        </div>

        {/* Enable/disable + preview the live guest app */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              checked={(form.guest_app_enabled ?? 1) !== 0}
              onChange={e => set('guest_app_enabled', e.target.checked ? 1 : 0)} />
            {t('portal.manage.properties.guestAppEnabled', 'Guía digital activada para los huéspedes')}
          </label>
          {form.id && <GuestAppPreview propertyId={form.id} token={form.guest_token ?? null} onToken={(tok) => set('guest_token', tok)} />}
        </div>

        <GuestSectionsConfig value={form.guest_sections_json ?? null} onChange={v => set('guest_sections_json', v)} />

        {/* Check-in & access */}
        <div className="pt-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.properties.guideCheckinGroup', 'Entrada y acceso')}</p>
          <Field label={t('portal.manage.properties.fieldCheckinInstructions', 'Instrucciones de entrada (check-in)')}><AutoTextarea className={input} value={form.checkin_instructions ?? ''} onChange={e => set('checkin_instructions', e.target.value)} placeholder={t('portal.manage.properties.checkinInstructionsPlaceholder', 'Cómo llegar, dónde recoger las llaves, horarios…')} /></Field>
          <Field label={t('portal.manage.properties.fieldAccessInstructions')}><AutoTextarea className={input} value={form.access_instructions ?? ''} onChange={e => set('access_instructions', e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('portal.manage.properties.fieldDoorCode', 'Código de acceso / puerta')}><input className={input} value={form.door_code ?? ''} onChange={e => set('door_code', e.target.value)} placeholder={t('portal.manage.properties.doorCodePlaceholder', 'p. ej. 1234')} /></Field>
            <Field label={t('portal.manage.properties.fieldLockInfo', 'Información de la cerradura')}><input className={input} value={form.lock_info ?? ''} onChange={e => set('lock_info', e.target.value)} placeholder={t('portal.manage.properties.lockInfoPlaceholder', 'Cerradura inteligente, caja de llaves…')} /></Field>
          </div>
          <p className="text-xs text-slate-400 mt-1">{t('portal.manage.properties.sensitiveHint', 'El código y la cerradura solo se muestran en la app del huésped (enlace privado), nunca en tu web pública.')}</p>
        </div>

        {/* WiFi */}
        <div className="pt-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.properties.guideWifiGroup', 'WiFi')}</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('portal.manage.properties.fieldWifiName')}><input className={input} value={form.wifi_name ?? ''} onChange={e => set('wifi_name', e.target.value)} /></Field>
            <Field label={t('portal.manage.properties.fieldWifiPassword')}><input className={input} value={form.wifi_password ?? ''} onChange={e => set('wifi_password', e.target.value)} /></Field>
          </div>
        </div>

        {/* Parking */}
        <div className="pt-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.properties.guideParkingGroup', 'Aparcamiento')}</p>
          <Field label={t('portal.manage.properties.fieldParkingInfo', 'Información de aparcamiento')}><AutoTextarea className={input} value={form.parking_info ?? ''} onChange={e => set('parking_info', e.target.value)} placeholder={t('portal.manage.properties.parkingInfoPlaceholder', 'Dónde aparcar, si es gratuito o de pago, plazas…')} /></Field>
        </div>

        {/* House manual */}
        <div className="pt-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.properties.guideManualGroup', 'Manual de la casa')}</p>
          <Field label={t('portal.manage.properties.fieldHouseManual', 'Manual de la casa')}><AutoTextarea className={input} value={form.house_manual ?? ''} onChange={e => set('house_manual', e.target.value)} placeholder={t('portal.manage.properties.houseManualPlaceholder', 'Cómo funciona el aire acondicionado, la calefacción, la basura, electrodomésticos…')} /></Field>
        </div>

        {/* Local guide */}
        <div className="pt-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.properties.guideLocalGroup', 'Guía local')}</p>
          <Field label={t('portal.manage.properties.fieldNeighborhood')}><AutoTextarea className={input} value={form.neighborhood_tips ?? ''} onChange={e => set('neighborhood_tips', e.target.value)} /></Field>
          <Field label={t('portal.manage.properties.fieldLocalGuide', 'Recomendaciones locales')}><AutoTextarea className={input} value={form.local_guide ?? ''} onChange={e => set('local_guide', e.target.value)} placeholder={t('portal.manage.properties.localGuidePlaceholder', 'Restaurantes, supermercados, transporte, playas, qué visitar…')} /></Field>
        </div>

        {/* Check-out */}
        <div className="pt-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.properties.guideCheckoutGroup', 'Salida')}</p>
          <Field label={t('portal.manage.properties.fieldCheckoutInstructions', 'Instrucciones de salida (check-out)')}><AutoTextarea className={input} value={form.checkout_instructions ?? ''} onChange={e => set('checkout_instructions', e.target.value)} placeholder={t('portal.manage.properties.checkoutInstructionsPlaceholder', 'Dónde dejar las llaves, basura, ventanas, electrodomésticos…')} /></Field>
        </div>

        {/* Emergency contact */}
        <div className="pt-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('portal.manage.properties.guideEmergencyGroup', 'Contacto de emergencia')}</p>
          <Field label={t('portal.manage.properties.fieldEmergencyContact', 'Contacto de emergencia')}><AutoTextarea className={input} value={form.emergency_contact ?? ''} onChange={e => set('emergency_contact', e.target.value)} placeholder={t('portal.manage.properties.emergencyContactPlaceholder', 'Teléfono 24h, urgencias, mantenimiento…')} /></Field>
        </div>

        {!form.id && (
          <p className="text-xs text-slate-400">{t('portal.manage.properties.guidePreviewLocked', 'Guarda el alojamiento para previsualizar la guía del huésped.')}</p>
        )}
      </EditorSection>

      {/* Automated guest messaging (per property) */}
      <EditorSection icon={MessageSquare} title={t('portal.manage.properties.messagingTitle', 'Mensajes automáticos al huésped')}>
        <p className="text-sm text-slate-500 -mt-1">
          {t('portal.manage.properties.messagingHint', 'Enviamos automáticamente un mensaje de bienvenida antes de la llegada (con la guía y el registro de viajeros) y un agradecimiento tras la salida.')}
        </p>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
          <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            checked={(form.guest_messaging_enabled ?? 1) !== 0}
            onChange={e => set('guest_messaging_enabled', e.target.checked ? 1 : 0)} />
          {t('portal.manage.properties.messagingEnabled', 'Mensajes automáticos activados para este alojamiento')}
        </label>
      </EditorSection>

      {/* Policies (per property) */}
      <EditorSection icon={ScrollText} title={t('portal.manage.properties.policiesTitle', 'Normas y políticas')}>
        <Field label={t('portal.manage.properties.fieldHouseRules')}><AutoTextarea className={input} value={form.house_rules ?? ''} onChange={e => set('house_rules', e.target.value)} /></Field>
        <Field label={t('portal.manage.properties.fieldCancellation', 'Política de cancelación')}><AutoTextarea className={input} value={form.cancellation_policy ?? ''} onChange={e => set('cancellation_policy', e.target.value)} /></Field>
      </EditorSection>

      {/* Calendar sync (iCal) */}
      <EditorSection icon={RefreshCw} title={t('portal.manage.properties.calendarSyncTitle', 'Sincronización de calendario (iCal)')}>
        <p className="text-sm text-slate-500 -mt-1">{t('portal.manage.properties.calendarSyncHint', 'Importa los calendarios de tus otros canales para evitar dobles reservas. Aquí también tienes tu enlace de exportación.')}</p>

        {/* Export URL — prominent, this is what owners give to their channels */}
        <IcalExport guestToken={form.guest_token ?? null} />

        {/* Import URLs — pull OTA calendars in */}
        <div className="pt-2">
          <p className="text-sm font-semibold text-slate-700">{t('portal.manage.properties.icalImportTitle', 'Importar calendarios de tus canales')}</p>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">{t('portal.manage.properties.icalImportHint', 'Pega aquí la URL de exportación .ics de cada plataforma. Las sincronizamos a diario.')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Field label={t('portal.manage.properties.icalBooking', 'Importar de Booking.com')}><input className={input} value={form.ical_import_booking ?? ''} onChange={e => set('ical_import_booking', e.target.value)} placeholder="https://…" /></Field>
              <ChannelHelp summary={t('portal.manage.properties.icalHelpBookingLabel', '¿Dónde encuentro la URL de Booking.com?')} text={t('portal.manage.properties.icalHelpBooking', 'Extranet → Tarifas y disponibilidad → Sincronización de calendarios → Exportar.')} />
            </div>
            <div>
              <Field label={t('portal.manage.properties.icalAirbnb', 'Importar de Airbnb')}><input className={input} value={form.ical_import_airbnb ?? ''} onChange={e => set('ical_import_airbnb', e.target.value)} placeholder="https://…" /></Field>
              <ChannelHelp summary={t('portal.manage.properties.icalHelpAirbnbLabel', '¿Dónde encuentro la URL de Airbnb?')} text={t('portal.manage.properties.icalHelpAirbnb', 'Calendario → Disponibilidad → Sincronizar calendarios → Exportar calendario (copia el enlace .ics).')} />
            </div>
            <div>
              <Field label={t('portal.manage.properties.icalVrbo', 'Importar de Vrbo')}><input className={input} value={form.ical_import_vrbo ?? ''} onChange={e => set('ical_import_vrbo', e.target.value)} placeholder="https://…" /></Field>
              <ChannelHelp summary={t('portal.manage.properties.icalHelpVrboLabel', '¿Dónde encuentro la URL de Vrbo?')} text={t('portal.manage.properties.icalHelpVrbo', 'Calendario → Importar/Exportar → copia la URL de exportación.')} />
            </div>
            <div>
              <Field label={t('portal.manage.properties.icalOther', 'Importar de otro')}><input className={input} value={form.ical_import_other ?? ''} onChange={e => set('ical_import_other', e.target.value)} placeholder="https://…" /></Field>
              <ChannelHelp summary={t('portal.manage.properties.icalHelpOtherLabel', '¿Dónde encuentro la URL?')} text={t('portal.manage.properties.icalHelpOther', 'Busca la opción «Exportar calendario» o «iCal» en tu plataforma y copia el enlace .ics.')} />
            </div>
          </div>
        </div>
      </EditorSection>

      {/* Guest portal link — only once the property exists (needs an id) */}
      {form.id && <GuestPortalLink propertyId={form.id} initialToken={form.guest_token ?? null} onToken={(tok) => set('guest_token', tok)} />}

      {/* Media — only once the property exists (needs an id) */}
      {form.id ? (
        <MediaManager propertyId={form.id} />
      ) : (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500 text-center">
          {t('portal.manage.properties.mediaLocked')}
        </div>
      )}

      </div>{/* /columns */}

      {/* Sticky save */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur border-t border-slate-200 p-4 pr-20 lg:pr-4 lg:left-64">
        <div className="max-w-7xl mx-auto flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900">{t('portal.manage.properties.cancel')}</button>
          <SaveButton
            onClick={onSave}
            saving={save.isPending}
            isDirty={isDirty && nameValid}
            label={t('portal.manage.properties.save')}
            savedLabel={t('common.saved', 'Guardado')}
          />
        </div>
      </div>
    </div>
  );
}

// Grouped, selectable amenity chips. Stores the selected keys (the parent
// serialises them into amenities_json).
function AmenityPicker({ selected, onChange }: { selected: string[]; onChange: (keys: string[]) => void }) {
  const { t } = useTranslation();
  const sel = new Set(selected);
  const toggle = (key: string) => {
    const next = new Set(sel);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange([...next]);
  };
  return (
    <div className="space-y-4">
      {AMENITY_GROUPS.map(group => {
        const items = AMENITIES.filter(a => a.group === group.key);
        if (items.length === 0) return null;
        return (
          <div key={group.key}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t(`amenities.groups.${group.key}`, group.label)}</p>
            <div className="flex flex-wrap gap-2">
              {items.map(a => {
                const on = sel.has(a.key);
                const Icon = a.icon;
                return (
                  <button key={a.key} type="button" onClick={() => toggle(a.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      on ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-300 hover:border-teal-400'
                    }`}>
                    <Icon size={14} /> {t(`amenities.items.${a.key}`, a.label)}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Read-only export URL (the property's public iCal feed) with copy. Available
// once the property has a guest_token (generated via the guest portal section).
function IcalExport({ guestToken }: { guestToken: string | null }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  if (!guestToken) {
    return (
      <p className="text-sm text-slate-500">
        {t('portal.manage.properties.icalExportLocked', 'Genera el enlace del portal del huésped (más abajo) para obtener tu URL de exportación de calendario.')}
      </p>
    );
  }
  const url = `https://app.kanarix.es/api/public/ical/${guestToken}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* clipboard unavailable */ }
  };
  return (
    <div className="rounded-xl border border-teal-200 bg-teal-50 p-4">
      <p className="text-sm font-semibold text-teal-900">{t('portal.manage.properties.icalExport', 'Tu URL de exportación')}</p>
      <p className="text-xs text-teal-800/90 mt-0.5 mb-2.5">{t('portal.manage.properties.icalExportOneLine', 'Pega esta URL en Booking/Airbnb/Vrbo para que importen tus reservas directas.')}</p>
      <div className="flex items-center gap-2">
        <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} className="flex-1 px-3 py-2 rounded-lg border border-teal-300 bg-white font-mono text-xs text-slate-700" />
        <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold shrink-0">
          {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? t('portal.manage.properties.copied', 'Copiado') : t('portal.manage.properties.copy', 'Copiar')}
        </button>
      </div>
    </div>
  );
}

// Collapsible per-channel help: explains where each platform's iCal export URL is.
function ChannelHelp({ summary, text }: { summary: string; text: string }) {
  return (
    <details className="mt-1.5 text-xs text-slate-500">
      <summary className="cursor-pointer select-none text-teal-700 hover:text-teal-800">{summary}</summary>
      <p className="mt-1 text-slate-500 leading-relaxed">{text}</p>
    </details>
  );
}

function SeasonsEditor({ propertyId, basePrice }: { propertyId: number; basePrice: number | null }) {
  const { t } = useTranslation();
  const { data: seasons, isLoading } = useSeasons(propertyId);
  const saveSeason = useSaveSeason();
  const deleteSeason = useDeleteSeason();
  const monthName = (m: number): string => t(`portal.manage.pricing.months.${m}`, '') || String(m);

  const list = seasons ?? [];

  const addSeason = () => {
    saveSeason.mutate({
      name: t('portal.manage.pricing.newSeason', 'Nueva temporada'),
      start_month: 1,
      end_month: 1,
      price_per_night: basePrice ?? 100,
      min_stay: 2,
      sort_order: (list.length ?? 0) + 1,
      property_id: propertyId,
    });
  };

  const patch = (s: Season, field: keyof Season, value: number | string) =>
    saveSeason.mutate({ id: s.id, property_id: propertyId, [field]: value });

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 break-inside-avoid mb-6">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <CalendarRange size={17} className="text-teal-600" /> {t('portal.manage.pricing.seasonsTitle', 'Precios por temporada')}
        </h2>
        <button onClick={addSeason} disabled={saveSeason.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm font-medium px-3 py-1.5">
          <Plus size={15} /> {t('portal.manage.pricing.addSeason', 'Añadir temporada')}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-teal-500" size={22} /></div>
      ) : list.length === 0 ? (
        <p className="text-sm text-slate-500">{t('portal.manage.pricing.emptyAddFirst', 'Añade tu primera temporada de precios.')}</p>
      ) : (
        <div className="space-y-2">
          {list.map((s) => (
            <div key={s.id} className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
              <label className="flex-1 min-w-[8rem]">
                <span className="block text-xs font-medium text-slate-500 mb-1">{t('portal.manage.pricing.colSeason', 'Temporada')}</span>
                <input className={input} defaultValue={s.name}
                  onBlur={(e) => { if (e.target.value !== s.name) patch(s, 'name', e.target.value); }} />
              </label>
              <label>
                <span className="block text-xs font-medium text-slate-500 mb-1">{t('portal.manage.pricing.colMonths', 'Meses')}</span>
                <div className="flex items-center gap-1.5">
                  <select className={input} value={s.start_month}
                    onChange={(e) => patch(s, 'start_month', Number(e.target.value))}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
                  </select>
                  <span className="text-slate-400">–</span>
                  <select className={input} value={s.end_month}
                    onChange={(e) => patch(s, 'end_month', Number(e.target.value))}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
                  </select>
                </div>
              </label>
              <label className="w-28">
                <span className="block text-xs font-medium text-slate-500 mb-1">{t('portal.manage.pricing.colPricePerNight', '€ / noche')}</span>
                <input type="number" className={input} defaultValue={s.price_per_night}
                  onBlur={(e) => { const v = Number(e.target.value); if (v !== s.price_per_night) patch(s, 'price_per_night', v); }} />
              </label>
              <label className="w-24">
                <span className="block text-xs font-medium text-slate-500 mb-1">{t('portal.manage.pricing.colMinStay', 'Estancia mín.')}</span>
                <input type="number" className={input} defaultValue={s.min_stay}
                  onBlur={(e) => { const v = Number(e.target.value); if (v !== s.min_stay) patch(s, 'min_stay', v); }} />
              </label>
              <button onClick={() => { if (confirm(t('portal.manage.pricing.deleteConfirm', '¿Eliminar esta temporada de precios?'))) deleteSeason.mutate(s.id); }} disabled={deleteSeason.isPending}
                aria-label={t('portal.manage.pricing.delete', 'Eliminar')}
                className="mb-1 text-slate-300 hover:text-rose-500 disabled:opacity-50"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function MediaManager({ propertyId }: { propertyId: number }) {
  const { t } = useTranslation();
  const { data: media } = usePropertyMedia(propertyId);
  const upload = useUploadPropertyMedia();
  const remove = useDeletePropertyMedia(propertyId);
  const [err, setErr] = useState('');

  const onPick = async (kind: 'photo' | 'video' | 'document', e: React.ChangeEvent<HTMLInputElement>) => {
    setErr('');
    const files = Array.from(e.target.files ?? []);
    for (const file of files) {
      try { await upload.mutateAsync({ propertyId, kind, file }); }
      catch (x) { setErr(x instanceof Error ? `${file.name}: ${x.message}` : t('portal.manage.properties.uploadError')); }
    }
    e.target.value = '';
  };

  const byKind = (k: string) => (media ?? []).filter((m: PropertyMedia) => m.kind === k);
  const Picker = ({ kind, icon: Icon, label, accept }: { kind: 'photo' | 'video' | 'document'; icon: LucideIcon; label: string; accept: string }) => (
    <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 cursor-pointer">
      <Icon size={15} /> {label}
      <input type="file" accept={accept} multiple className="hidden" onChange={e => onPick(kind, e)} />
    </label>
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 break-inside-avoid mb-6">
      <h2 className="font-semibold text-slate-900">{t('portal.manage.properties.mediaTitle')}</h2>
      <div className="flex flex-wrap gap-2">
        <Picker kind="photo" icon={ImageIcon} label={t('portal.manage.properties.mediaPhotos')} accept="image/*" />
        <Picker kind="video" icon={Film} label={t('portal.manage.properties.mediaVideos')} accept="video/mp4,video/webm,video/quicktime" />
        <Picker kind="document" icon={FileText} label={t('portal.manage.properties.mediaDocuments')} accept="application/pdf,.doc,.docx" />
        {upload.isPending && <span className="flex items-center gap-1.5 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> {t('portal.manage.properties.uploading')}</span>}
      </div>
      {err && <p className="text-sm text-rose-600">{err}</p>}

      {/* Photos grid */}
      {byKind('photo').length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {byKind('photo').map(m => (
            <div key={m.id} className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100">
              <img src={`/api/portal/manage/property-media/${m.id}`} alt={m.filename ?? ''} loading="lazy" className="h-full w-full object-cover" />
              <button onClick={() => { if (confirm(t('portal.manage.properties.deleteMediaConfirm', '¿Eliminar este archivo?'))) remove.mutate(m.id); }} aria-label={t('portal.manage.properties.deleteMedia')} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      {/* Videos + documents list */}
      {[...byKind('video'), ...byKind('document')].map(m => (
        <div key={m.id} className="flex items-center justify-between gap-2 text-sm border border-slate-100 rounded-lg px-3 py-2">
          <a href={`/api/portal/manage/property-media/${m.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-teal-600 truncate">
            {m.kind === 'video' ? <Film size={14} /> : <FileText size={14} />} <span className="truncate">{m.filename}</span>
          </a>
          <button onClick={() => { if (confirm(t('portal.manage.properties.deleteMediaConfirm', '¿Eliminar este archivo?'))) remove.mutate(m.id); }} aria-label={t('portal.manage.properties.deleteMedia')} className="text-slate-300 hover:text-rose-500 shrink-0"><Trash2 size={14} /></button>
        </div>
      ))}
    </section>
  );
}

// "Vista previa de la guía" button. Opens the live guest app at /guest/<token>,
// generating the token first (idempotently, via the existing endpoint) if the
// property doesn't have one yet — same flow as the guest-portal link block.
function GuestAppPreview({ propertyId, token, onToken }: { propertyId: number; token: string | null; onToken: (token: string) => void }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const open = async () => {
    let tok = token;
    if (!tok) {
      setLoading(true);
      try {
        const res = await fetch('/api/portal/manage/property-guest-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ property_id: propertyId }),
        });
        const data = await res.json() as { guest_token?: string };
        if (data.guest_token) { tok = data.guest_token; onToken(tok); }
      } catch { /* ignore — button just won't open */ }
      finally { setLoading(false); }
    }
    if (tok) window.open(`/guest/${tok}`, '_blank', 'noopener');
  };

  return (
    <button type="button" onClick={open} disabled={loading}
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-teal-300 text-teal-700 hover:bg-teal-50 disabled:opacity-60 text-sm font-semibold">
      {loading ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} {t('portal.manage.properties.guidePreview', 'Vista previa de la guía')}
    </button>
  );
}

function GuestPortalLink({ propertyId, initialToken, onToken }: { propertyId: number; initialToken: string | null; onToken: (token: string) => void }) {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(initialToken);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const url = token ? `https://app.kanarix.es/guest/${token}` : '';

  const generate = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetch('/api/portal/manage/property-guest-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ property_id: propertyId }),
      });
      const data = await res.json() as { guest_token?: string; error?: string };
      if (!res.ok || !data.guest_token) throw new Error(data.error || 'error');
      setToken(data.guest_token);
      onToken(data.guest_token);
    } catch {
      setErr(t('portal.manage.properties.guestPortalError', 'No se pudo generar el enlace. Inténtalo de nuevo.'));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 break-inside-avoid mb-6">
      <div>
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><QrCode size={17} className="text-teal-600" /> {t('portal.manage.properties.guestPortalTitle', 'Portal del huésped')}</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          {t('portal.manage.properties.guestPortalHint', 'Comparte este enlace con tus huéspedes. Verán el WiFi, las instrucciones de acceso, las normas y tus datos de contacto.')}
        </p>
      </div>

      {!token ? (
        <button
          onClick={generate}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-lg"
        >
          {loading && <Loader2 size={16} className="animate-spin" />} {t('portal.manage.properties.guestPortalGenerate', 'Generar/Ver enlace del huésped')}
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <input readOnly value={url} className="flex-1 min-w-0 bg-transparent text-sm text-slate-700 outline-none" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={copy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold">
              {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? t('portal.manage.properties.copied', 'Copiado') : t('portal.manage.properties.guestPortalCopy', 'Copiar enlace')}
            </button>
            <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-semibold">
              <ExternalLink size={15} /> {t('portal.manage.properties.open', 'Abrir')}
            </a>
          </div>
        </div>
      )}

      {err && <p className="text-sm text-rose-600">{err}</p>}
    </section>
  );
}
